import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronUp, ChevronDown } from 'lucide-react';
import CommanderCardWithMenu from '../CommanderCardWithMenu';
import CardOverlays from '../../../../ui/CardOverlays';
import ManaSymbols from '../../../../ui/ManaSymbols';
import './CommandBridge.css';

/**
 * Commander-Sektion der Brücke: vorderste Karte voll sichtbar, weitere
 * Commander als Titelstreifen darüber (Klick tauscht). Kein Kopf-Label —
 * die Karte ist ihr eigener Kopf; Zuklappen über Glas-Chip auf dem Bild.
 * Der Karten-Slot bleibt Drop-Ziel — Droppable-ID `category-commander`,
 * erkannt von `isCommanderDrop` (`utils/dragCardUtils.js`) über
 * `data.current.commander === true`.
 */
export default function CommanderStack({ cards, deckId, isOwnProfile, canEditDeck, onRemoveCommander, onShowDetails, onSwitchPrint, isDraggingCard }) {
  const commanders = (cards || []).filter((c) => c.isCommander);
  const [collapsed, setCollapsed] = useState(false);
  const [frontId, setFrontId] = useState(null);
  const { setNodeRef, isOver } = useDroppable({ id: 'category-commander', data: { commander: true } });

  const front = commanders.find((c) => c.id === frontId) || commanders[0] || null;
  const back = commanders.filter((c) => front && c.id !== front.id);
  const showAmbientHint = isDraggingCard && !isOver;
  const dropStyle = {
    outline: isOver ? '2px solid var(--accent)' : showAmbientHint ? '2px dashed var(--accent)' : 'none',
    outlineOffset: '2px',
    backgroundColor: isOver || showAmbientHint ? 'var(--accent-tint)' : 'transparent',
    borderRadius: 'var(--radius-md)',
  };

  if (commanders.length === 0) {
    if (!isOwnProfile || !canEditDeck) return null;
    return (
      <div ref={setNodeRef} className="bridge-commander__empty" style={dropStyle}>
        Commander wählen — Karte hierher ziehen oder im Kartenmenü „Als Commander setzen"
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="bridge-section">
        {commanders.map((c) => (
          <button
            key={c.id} type="button" className="bridge-section__toggle bridge-commander__row"
            onClick={() => setCollapsed(false)} aria-expanded={false}
          >
            <span className="bridge-commander__name">{c.name}</span>
            <span className="bridge-section__value">
              <ManaSymbols manaCost={c.manaCost} size={13} />
              <ChevronDown size={13} aria-hidden />
            </span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div ref={setNodeRef} className="bridge-commander" style={dropStyle}>
      {back.map((c) => (
        <button
          key={c.id} type="button" className="bridge-commander__strip"
          title={`${c.name} nach vorn holen`}
          onClick={() => setFrontId(c.id)}
        >
          <span className="bridge-commander__name">{c.name}</span>
          <ManaSymbols manaCost={c.manaCost} size={13} />
        </button>
      ))}
      <div className="bridge-commander__card">
        <CommanderCardWithMenu
          card={front} deckId={deckId} isOwnProfile={isOwnProfile}
          onRemove={onRemoveCommander} onShowDetails={onShowDetails} onSwitchPrint={onSwitchPrint}
          overlayExtra={
            <CardOverlays.Button icon={ChevronUp} label="Commander einklappen" onClick={() => setCollapsed(true)} />
          }
        />
      </div>
    </div>
  );
}
